'use client';

/**
 * useAuthHealth Hook
 *
 * Centralized auth health monitoring for call pages.
 * Detects authentication failures proactively and exposes state for UI components.
 *
 * Features:
 * - Periodic auth validation during active calls
 * - Tracks 401 errors from any API call
 * - Coordinates with AppShellProvider
 * - Provides easy re-authentication path
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import logger from '@/lib/logger';

// ============================================================================
// TYPES
// ============================================================================

export type AuthHealthStatus = 'healthy' | 'degraded' | 'expired' | 'unknown';

export interface AuthHealthState {
  /** Current auth health status */
  status: AuthHealthStatus;
  /** Whether authentication is healthy */
  isHealthy: boolean;
  /** Timestamp of last successful auth check */
  lastChecked: number;
  /** Timestamp of session expiry (if known) */
  expiresAt: number | null;
  /** Error message if auth is unhealthy */
  error: string | null;
  /** Whether re-authentication is possible without leaving the page */
  canReauth: boolean;
  /** Number of consecutive auth failures */
  failureCount: number;
  /** Source of the last failure (e.g., 'files', 'uploads', 'api') */
  lastFailureSource: string | null;
}

export interface UseAuthHealthOptions {
  /**
   * Enable periodic health checks (default: true)
   */
  enabled?: boolean;

  /**
   * Interval for health checks in milliseconds (default: 10 minutes)
   */
  checkInterval?: number;

  /**
   * Whether this is for an active call session (default: true)
   */
  isActiveCall?: boolean;
}

export interface UseAuthHealthReturn extends AuthHealthState {
  /**
   * Report an auth error from an API call
   */
  reportAuthError: (source: string, error?: string) => void;

  /**
   * Clear auth error state (e.g., after successful re-auth)
   */
  clearAuthError: () => void;

  /**
   * Manually trigger an auth check
   */
  checkAuthHealth: () => Promise<boolean>;

  /**
   * The call ended: silence call-state noise while retaining genuine authentication failures
   */
  markCallEnded: () => void;

  /**
   * A call is live again: auth problems are reported normally
   */
  markCallActive: () => void;

  /**
   * Defer only call-state / missing-session warnings while the bounded ended verdict is pending.
   * Release on every completion or cancellation; independent auth failures remain visible.
   */
  beginCallEndedCheck: () => () => void;

  /**
   * Attempt to re-authenticate
   */
  attemptReauth: () => Promise<boolean>;
}

/** Only these failures can be explained by a call ending; explicit auth errors are independent. */
function isCallStateNoise(state: AuthHealthState): boolean {
  return state.lastFailureSource === 'callState'
    || (state.lastFailureSource === null && state.error === 'Session not found');
}

/** Shared reset for successful re-authentication and clearing stale call-state noise. */
function clearAuthFailure(state: AuthHealthState): AuthHealthState {
  return {
    ...state,
    status: 'healthy',
    isHealthy: true,
    error: null,
    failureCount: 0,
    lastFailureSource: null,
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Default check interval: 10 minutes. API 401s still report immediately via reportAuthError(). */
const DEFAULT_CHECK_INTERVAL = 10 * 60 * 1000;

/** Number of failures before marking as expired */
const FAILURE_THRESHOLD = 3;

/** Debounce time for error reports to prevent flooding */
const ERROR_DEBOUNCE_MS = 1000;

/** Minimum time between status transitions to prevent flapping */
const STATUS_TRANSITION_DEBOUNCE_MS = 5000;

/**
 * Backoff delays (ms) between refreshSession retries. Tuned for the observed
 * failure mode: GoTrue→Postgres dial timeout (~10s) during heavy load. By the
 * time the second retry fires the brief blip is over.
 */
const REAUTH_RETRY_DELAYS_MS = [1000, 3000, 8000];

/**
 * True iff the error is worth retrying. Permanent errors (bad refresh token,
 * invalid grant, etc.) should fail fast so the user is prompted to log in.
 */
function isTransientReauthError(error: unknown): boolean {
  if (!error) return false;
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  // Permanent — do NOT retry
  const permanentPatterns = [
    'invalid_grant',
    'refresh_token_not_found',
    'refresh token not found',
    'invalid refresh token',
    'invalid_token',
    'expired_token',
    'user_not_found',
    'unauthorized',
  ];
  if (permanentPatterns.some((p) => message.includes(p))) return false;

  // Transient — retry
  const transientPatterns = [
    'network',
    'fetch failed',
    'failed to fetch',
    'timeout',
    'timed out',
    'gateway',
    '502',
    '503',
    '504',
    'econnreset',
    'econnrefused',
    'socket hang up',
    'aborted',
  ];
  if (transientPatterns.some((p) => message.includes(p))) return true;

  // Unknown — default to retrying once. The retry loop caps attempts anyway.
  return true;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook for monitoring authentication health during call sessions.
 *
 * @example
 * ```tsx
 * const { isHealthy, status, reportAuthError, attemptReauth } = useAuthHealth({
 *   enabled: isInCall,
 * });
 *
 * // Report a 401 error
 * if (response.status === 401) {
 *   reportAuthError('files', 'Session expired');
 * }
 *
 * // Show warning banner
 * if (!isHealthy) {
 *   return <AuthWarningBanner status={status} onReauth={attemptReauth} />;
 * }
 * ```
 */
export function useAuthHealth({
  enabled = true,
  checkInterval = DEFAULT_CHECK_INTERVAL,
  isActiveCall = true,
}: UseAuthHealthOptions = {}): UseAuthHealthReturn {
  const [state, setState] = useState<AuthHealthState>({
    status: 'unknown',
    isHealthy: true,
    lastChecked: 0,
    expiresAt: null,
    error: null,
    canReauth: true,
    failureCount: 0,
    lastFailureSource: null,
  });

  const checkInProgressRef = useRef(false);
  const lastErrorReportRef = useRef(new Map<string, number>());
  const pendingCallEndedChecksRef = useRef(new Set<symbol>());
  const [pendingCallEndedChecks, setPendingCallEndedChecks] = useState(0);
  const lastStatusTransitionRef = useRef(0);
  const isMountedRef = useRef(true);
  const callEndedRef = useRef(false);

  /**
   * Check authentication health by validating the session
   */
  const checkAuthHealth = useCallback(async (): Promise<boolean> => {
    if (checkInProgressRef.current) {
      return state.isHealthy;
    }

    checkInProgressRef.current = true;

    try {
      const supabase = createClient();
      const { data: { session }, error } = await supabase.auth.getSession();

      if (!isMountedRef.current) return false;
      // A gone room can leave no browser session. That alone is not evidence of token expiry.
      // Keep checking real sessions and explicit auth errors, including checks already in flight.
      if (callEndedRef.current && !session && !error) return true;

      const now = Date.now();

      if (error || !session) {
        logger.debug('useAuthHealth', 'Auth check failed', { error: error?.message });

        setState(prev => {
          // An empty cache read cannot supersede independent authentication evidence.
          if (!session && !error && !prev.isHealthy && !isCallStateNoise(prev)) return prev;

          const newFailureCount = prev.failureCount + 1;

          // Debounce status transitions to prevent rapid flapping
          const timeSinceLastTransition = now - lastStatusTransitionRef.current;
          const canTransition = timeSinceLastTransition >= STATUS_TRANSITION_DEBOUNCE_MS || prev.status === 'unknown';
          const newStatus: AuthHealthStatus = canTransition
            ? (newFailureCount >= FAILURE_THRESHOLD ? 'expired' : 'degraded')
            : prev.status;

          if (canTransition && newStatus !== prev.status) {
            lastStatusTransitionRef.current = now;
          }

          return {
            ...prev,
            status: newStatus,
            isHealthy: false,
            lastChecked: now,
            error: error?.message || 'Session not found',
            failureCount: newFailureCount,
            lastFailureSource: null,
          };
        });

        return false;
      }

      // Session is valid
      const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
      const timeUntilExpiry = expiresAt ? expiresAt - now : null;

      // Warn if session expires soon (within 5 minutes)
      const isSoonExpiring = timeUntilExpiry !== null && timeUntilExpiry < 5 * 60 * 1000;

      setState({
        status: isSoonExpiring ? 'degraded' : 'healthy',
        isHealthy: !isSoonExpiring,
        lastChecked: now,
        expiresAt,
        error: isSoonExpiring ? 'Session expiring soon' : null,
        canReauth: true,
        failureCount: 0,
        lastFailureSource: null,
      });

      logger.debug('useAuthHealth', 'Auth check successful', {
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : 'unknown',
        timeUntilExpiry: timeUntilExpiry ? Math.round(timeUntilExpiry / 1000) + 's' : 'unknown',
      });

      return true;
    } catch (err) {
      if (!isMountedRef.current) return false;

      const message = err instanceof Error ? err.message : 'Unknown error';
      logger.error('useAuthHealth', 'Auth check error', { error: message });

      setState(prev => ({
        ...prev,
        status: 'degraded',
        isHealthy: false,
        lastChecked: Date.now(),
        error: message,
        failureCount: prev.failureCount + 1,
      }));

      return false;
    } finally {
      checkInProgressRef.current = false;
    }
  }, [state.isHealthy]);

  /**
   * Report an authentication error from an API call
   */
  const reportAuthError = useCallback((source: string, error?: string) => {
    // Ended-call 401s must not hide independent archive, upload or file authentication failures.
    if (callEndedRef.current && source === 'callState') return;
    const now = Date.now();

    // Debounce repeated reports from the same producer. Call-state noise must never
    // consume the slot for a subsequent, independently confirmed refresh rejection.
    const lastReport = lastErrorReportRef.current.get(source);
    if (lastReport !== undefined && now - lastReport < ERROR_DEBOUNCE_MS) {
      return;
    }
    lastErrorReportRef.current.set(source, now);

    logger.warn('useAuthHealth', 'Auth error reported', { source, error });

    setState(prev => {
      // A generic call-state 401 cannot replace an independent failure that still
      // needs re-authentication. Keep that evidence until auth succeeds or resets;
      // otherwise markCallEnded would mistake it for noise and erase the warning.
      if (source === 'callState' && prev.lastFailureSource !== null && prev.lastFailureSource !== 'callState') return prev;

      const newFailureCount = prev.failureCount + 1;

      // Debounce status transitions to prevent rapid flapping
      const timeSinceLastTransition = now - lastStatusTransitionRef.current;
      const canTransition = timeSinceLastTransition >= STATUS_TRANSITION_DEBOUNCE_MS || prev.status === 'unknown';

      // During active calls, stay in 'degraded' and attempt recovery rather than jumping to 'expired'
      const newStatus: AuthHealthStatus = canTransition
        ? (newFailureCount >= FAILURE_THRESHOLD && !isActiveCall ? 'expired' : 'degraded')
        : prev.status;

      if (canTransition && newStatus !== prev.status) {
        lastStatusTransitionRef.current = now;
      }

      return {
        ...prev,
        status: newStatus,
        isHealthy: false,
        error: error || `Authentication failed (${source})`,
        failureCount: newFailureCount,
        lastFailureSource: source,
      };
    });
  }, [isActiveCall]);

  /**
   * Clear auth error state (e.g., after successful re-auth)
   */
  const clearAuthError = useCallback(() => {
    setState(clearAuthFailure);
  }, []);

  const markCallEnded = useCallback(() => {
    callEndedRef.current = true;
    setState(prev => {
      if (!isCallStateNoise(prev)) return prev;
      return clearAuthFailure(prev);
    });
  }, []);

  const markCallActive = useCallback(() => {
    callEndedRef.current = false;
  }, []);

  const beginCallEndedCheck = useCallback(() => {
    const check = Symbol('call-ended check');
    pendingCallEndedChecksRef.current.add(check);
    setPendingCallEndedChecks(pendingCallEndedChecksRef.current.size);
    return () => {
      if (!pendingCallEndedChecksRef.current.delete(check)) return;
      setPendingCallEndedChecks(pendingCallEndedChecksRef.current.size);
    };
  }, []);

  /**
   * Attempt to re-authenticate by refreshing the session.
   *
   * Retries transient failures (GoTrue→Postgres dial timeouts, network blips,
   * 5xx gateway errors) with backoff. Fails fast on permanent errors so we
   * don't waste retries on invalid_grant / expired_token. The observed failure
   * mode is GoTrue briefly losing its DB connection (~10s i/o timeout); by the
   * second retry the connection pool has recycled.
   */
  const attemptReauth = useCallback(async (): Promise<boolean> => {
    logger.debug('useAuthHealth', 'Attempting re-authentication');

    const supabase = createClient();
    const maxAttempts = REAUTH_RETRY_DELAYS_MS.length + 1; // initial + N retries
    let lastError: unknown = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        const delay = REAUTH_RETRY_DELAYS_MS[attempt - 1];
        logger.debug('useAuthHealth', 'Retrying refreshSession', { attempt, delayMs: delay });
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (!isMountedRef.current) return false;
      }

      try {
        const { data: { session }, error } = await supabase.auth.refreshSession();

        if (!error && session) {
          if (attempt > 0) {
            logger.info('useAuthHealth', 'Re-authentication succeeded after retry', { attempt });
          } else {
            logger.info('useAuthHealth', 'Re-authentication successful');
          }
          clearAuthError();
          return true;
        }

        lastError = error;
        if (!isTransientReauthError(error)) {
          logger.warn('useAuthHealth', 'Re-authentication failed (permanent)', { error: error?.message });
          break;
        }
        logger.warn('useAuthHealth', 'Re-authentication failed (transient, will retry)', {
          attempt,
          error: error?.message,
        });
      } catch (err) {
        lastError = err;
        if (!isTransientReauthError(err)) {
          break;
        }
        logger.warn('useAuthHealth', 'Re-authentication threw (transient, will retry)', {
          attempt,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    const finalMessage =
      lastError instanceof Error ? lastError.message :
      typeof lastError === 'object' && lastError && 'message' in lastError ? String((lastError as { message: unknown }).message) :
      'Please log in again';

    logger.error('useAuthHealth', 'Re-authentication exhausted retries', { error: finalMessage });

    setState((prev) => ({
      ...prev,
      canReauth: false,
      error: finalMessage,
    }));

    return false;
  }, [clearAuthError]);

  // Initial auth check on mount
  useEffect(() => {
    if (enabled) {
      checkAuthHealth();
    }
  }, [enabled, checkAuthHealth]);

  // Periodic health checks during active calls
  useEffect(() => {
    if (!enabled || !isActiveCall) return;

    const intervalId = setInterval(() => {
      checkAuthHealth();
    }, checkInterval);

    return () => clearInterval(intervalId);
  }, [enabled, isActiveCall, checkInterval, checkAuthHealth]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    // Retain the observation while checking, so a live, unauthorized or unreadable
    // verdict reveals it immediately. An ended verdict clears it through markCallEnded.
    ...(pendingCallEndedChecks > 0 && isCallStateNoise(state) ? clearAuthFailure(state) : state),
    reportAuthError,
    clearAuthError,
    markCallEnded,
    markCallActive,
    beginCallEndedCheck,
    checkAuthHealth,
    attemptReauth,
  };
}

export default useAuthHealth;
